/**
 * Organizer-driven advance propagation, extracted from location-view so
 * tests can exercise the every-5-seconds polling path without mounting
 * the full view (DOM + timers + team-sync + decryption side effects).
 *
 * Session 53 /investigate (2026-04-18) — user reported org's "advance
 * team" and "finish team" buttons didn't move the player. The backend
 * tests proved `orgAdvanceTeam` + `orgFinishTeam` + `getPlayerState`
 * correctly surface the new state. That left the PWA poll/handler as
 * the suspect, but the handler was inline in `location-view` with zero
 * direct tests. Extracted here so we can pin behavior contracts.
 */

import { getState, setState, type GameState } from '../store';
import { navigate } from '../router';
import { advanceLocationIndex, reconcileLocationIndex } from '../store-actions';
import { computeLocalAnchor } from './time-utils';
import { fetchPlayerState } from './auth-failure';

// PlayerStateResponse moved to ./auth-failure for the fetchPlayerState
// wrapper. Re-exported here so callers that already imported it from this
// path (reconcile-encrypted-hints.test.ts) continue to work without churn.
// Two lines (import + export) because a bare `export type { X } from './a'`
// makes X available to downstream importers but NOT inside this file —
// applyPlayerStatePoll's signature below references it directly.
import type { PlayerStateResponse } from './auth-failure';
export type { PlayerStateResponse };

/** Navigate delegate — overridable for tests. */
export type NavigateFn = (route: 'riddle' | 'results') => void;

/**
 * Apply one poll result to local state. Pure side-effect function:
 *   - if the server says the team finished → navigate('results') + stamp
 *     completedAt + penaltyMs
 *   - if the server's currentLocationIndex has moved past the client's
 *     (the org-advance case) → seed the new key into `nextKeys`, swap in
 *     the server-supplied encrypted riddle/hint, snap to the server's
 *     index and navigate('riddle')
 *   - if the server disagrees DOWN from the client (optimistic over-
 *     advance reconciliation) → same update path, trust server
 *   - otherwise → no-op
 *
 * Returns a short `PollOutcome` string so the test can assert which
 * branch fired, and to give the caller a hook for logging.
 */
export type PollOutcome = 'finished' | 'advanced' | 'no-change';

export function applyPlayerStatePoll(
  ps: PlayerStateResponse,
  navigateFn: NavigateFn = navigate,
  stateFn: () => GameState = getState,
): PollOutcome {
  const s = stateFn();

  if (ps.finishedAt) {
    // Session 67 post-ship /investigate — poller-driven finish (e.g. after
    // `orgFinishTeam` when the team WS missed the FINISH event) must also
    // seed `totalTimeMs` for the results screen. Without this, a teammate
    // whose poll discovered the finish first landed on «Базовое время
    // 00:00 / Итого 00:00». Formula mirrors backend buildLeaderboard.
    const penalty = ps.penaltyMs ?? s.penaltyMs;
    // Session 68 /review finding #2 — surface clock-skew anomalies to the
    // console. finishedAt < startedAt under normal operation is impossible
    // (single-server monotonic clock), so this only fires on DB restore
    // inconsistency or operator UPDATE. Without the warn, the Math.max
    // below silently renders «Базовое 00:00» and the organizer thinks
    // the UI is broken.
    if (
      typeof ps.startedAt === 'number' &&
      typeof ps.finishedAt === 'number' &&
      ps.finishedAt < ps.startedAt
    ) {
      console.warn('[poll-player-state] finished payload has finishedAt < startedAt',
        { finishedAt: ps.finishedAt, startedAt: ps.startedAt, skewMs: ps.startedAt - ps.finishedAt });
    }
    const total =
      typeof ps.startedAt === 'number' && typeof ps.finishedAt === 'number'
        ? Math.max(0, ps.finishedAt - ps.startedAt) + Math.max(0, penalty)
        : s.totalTimeMs;
    reconcileLocationIndex(ps.currentLocationIndex, {
      completedAt: ps.finishedAt,
      penaltyMs: penalty,
      status: 'FINISHED',
      totalTimeMs: total,
    });
    // /investigate 2026-04-26 prod game 757873 — guard against re-firing
    // navigate('results') when the player is ALREADY on the results screen.
    // Pre-fix: every 5-sec orgAdvancePoll tick after team finished called
    // navigate('results'); the router's same-hash branch then forced
    // handleRoute() → setView(renderResultsView) → boot animation replayed
    // every 5 seconds. Symptom user reported: «окно постоянно рефрешится с
    // определённой периодичностью». Hash check is the cheap symptom kill;
    // sibling fix in location-view.ts self-terminates the leaked poller so
    // the network noise stops too.
    if (typeof window === 'undefined' || window.location.hash !== '#/results') navigateFn('results');
    return 'finished';
  }

  if (ps.currentLocationIndex !== s.currentLocationIndex) {
    const newKeys = [...s.nextKeys];
    if (ps.nextRiddleKey) {
      if (ps.currentLocationIndex > 0) {
        newKeys[ps.currentLocationIndex - 1] = ps.nextRiddleKey;
      } else {
        setState({ firstRiddleKey: ps.nextRiddleKey });
      }
    }

    const newLocations = [...s.locations];
    const targetLoc = newLocations[ps.currentLocationIndex];
    if (targetLoc && ps.encryptedRiddleText) {
      newLocations[ps.currentLocationIndex] = {
        ...targetLoc,
        encryptedRiddle: ps.encryptedRiddleText,
        encryptedHint: ps.encryptedHintText || '',
        // v0.80.6 — mirror of the v0.80.5 reconcile fix for the poll path.
        // Online captain whose ADVANCE WS frame was lost (reconnect race /
        // org-driven advance) gets here via 5-sec poll. Without this merge,
        // hintTimer for the new location never fired because state.locations
        // [N+1].encryptedHints stayed at its (often empty / stale) prior
        // value. Fall back to existing local when server returned empty/
        // undefined (legacy server / location with zero Hints rows) — never
        // overwrite a populated local array with empty.
        encryptedHints: (ps.encryptedHints && ps.encryptedHints.length > 0)
          ? ps.encryptedHints
          : targetLoc.encryptedHints,
      };
    }

    // Session 53 /review #1 — three-tier anchor fallback so org-advance
    // restarts the new location's timer at 0:00, not at team-wide elapsed.
    //   1. locationEnteredAt + serverNow → clock-skew-corrected via
    //      computeLocalAnchor (match the JoinGameResponse path)
    //   2. startedAt → legacy fallback (wrong for post-first locations
    //      but preserves pre-session-53 behaviour)
    //   3. Date.now() → last resort
    const anchor =
      computeLocalAnchor(ps.locationEnteredAt, ps.serverNow)
      ?? ps.startedAt
      ?? Date.now();
    // /review 2026-04-27 v0.79.16 (F4) — sync server-authoritative geoVerified
    // for the NEW stage, mirroring reconcileFromServer (location-view.ts:248-
    // 266). When org mass-advances N→N+1 right after a teammate verified the
    // new stage's geo, server's /player-state reports geoVerifiedLat/Lon or
    // geoVerifiedAddress for the new stage too. Pre-fix: poll cleared
    // geoVerified but never seeded the fresh value → reopened tab fell into
    // riddle phase and required a redundant re-verify. ADDRESS-only stages
    // ship lat=null, lon=null, with a non-empty address — both numeric and
    // address shapes count as verified.
    const hasNumericCoords = ps.geoVerifiedLat != null && ps.geoVerifiedLon != null;
    const hasAddress = ps.geoVerifiedAddress != null && ps.geoVerifiedAddress.length > 0;
    const geoFromServer: { lat: number | null; lon: number | null; address?: string } | undefined =
      (hasNumericCoords || hasAddress)
        ? {
            lat: hasNumericCoords ? ps.geoVerifiedLat! : null,
            lon: hasNumericCoords ? ps.geoVerifiedLon! : null,
            ...(hasAddress ? { address: ps.geoVerifiedAddress! } : {}),
          }
        : undefined;
    // Group-3 bug class — per-location state MUST clear on EVERY index
    // change, both forward (serverIdx > current) AND backward
    // (optimistic-over-advance reconciliation). reconcileLocationIndex
    // handles the forward case + clock-skew-corrected timer seeding (server
    // is authoritative on the new location's geoVerified). For the down-
    // reconcile we use advanceLocationIndex — semantically the same shape
    // as an advance (need fresh per-location state at the corrected index).
    if (ps.currentLocationIndex > s.currentLocationIndex) {
      // Forward advance (org-driven) — use reconcileLocationIndex for clock correction
      reconcileLocationIndex(ps.currentLocationIndex, {
        locationStartedAt: anchor,
        penaltyMs: ps.penaltyMs ?? s.penaltyMs,
        nextKeys: newKeys,
        locations: newLocations,
        // /investigate 2026-04-27 iPhone Я.Карты — per-location state must be
        // cleared on EVERY advance path. `goTo` (WS-driven) already does this;
        // this poller-driven path was the missing leg.
        // /review 2026-04-27 v0.79.16 (F3) — `hintNavigateData` is the SIBLING
        // per-location field; renderLocationView at line ~807 reads it BEFORE
        // `geoVerified` and flips phase to 'site-code' if truthy, so the
        // v0.79.13 fix that only cleared `geoVerified` was incomplete — stale
        // hintNavigateData from stage N still corrupts stage N+1 with old
        // auto-navigate coords. Clear both for symmetry with `goTo`.
        geoVerified: geoFromServer,
        hintNavigateData: undefined,
      });
    } else {
      // Down-reconcile after optimistic over-advance — semantically the same as
      // an advance: we're moving to a different index and need fresh per-location
      // state. advanceLocationIndex's strict-reset is what we want here.
      advanceLocationIndex(ps.currentLocationIndex, {
        locationStartedAt: anchor,
        penaltyMs: ps.penaltyMs ?? s.penaltyMs,
        nextKeys: newKeys,
        locations: newLocations,
      });
    }
    navigateFn('riddle');
    return 'advanced';
  }

  // /investigate 2026-04-26 — defensive recovery for the WS-mirror gap.
  // Indices match (no advance) but the local `encryptedRiddle` for the
  // current stage is empty — that means the WS-driven advance left the
  // riddle stranded (server strips encryptedRiddleText for `i > 0` in
  // online-mode join, see GameJoinOps.kt:325). The 5-second poll's
  // `pollPlayerStateOnce` re-emits the missing payload from /player-state
  // so the next `renderLocationView` decrypts cleanly. Without this the
  // index-mismatch guard above would never fire (both server and client
  // are at the same index), and the player would stay stuck on
  // 'Ошибка дешифрования загадки' until they F5.
  const localLoc = s.locations[s.currentLocationIndex];
  if (
    localLoc &&
    !localLoc.encryptedRiddle &&
    ps.encryptedRiddleText &&
    ps.currentLocationIndex === s.currentLocationIndex
  ) {
    const newKeys = [...s.nextKeys];
    if (ps.nextRiddleKey) {
      if (ps.currentLocationIndex > 0) {
        newKeys[ps.currentLocationIndex - 1] = ps.nextRiddleKey;
      } else {
        setState({ firstRiddleKey: ps.nextRiddleKey });
      }
    }
    const newLocations = [...s.locations];
    newLocations[ps.currentLocationIndex] = {
      ...localLoc,
      encryptedRiddle: ps.encryptedRiddleText,
      encryptedHint: ps.encryptedHintText || localLoc.encryptedHint,
    };
    setState({ nextKeys: newKeys, locations: newLocations });
    return 'advanced';
  }

  return 'no-change';
}

/**
 * Fetch one `/player-state` cycle and apply the result. Swallows network
 * errors (poll should degrade silently across a brief offline blip).
 * Returns the outcome of the application, or `'error'` if the fetch
 * itself failed (auth, network, non-ok status).
 */
export async function pollPlayerStateOnce(
  gameCode: string,
  playerId: string,
  playerToken: string,
  fetchFn: typeof fetch = fetch,
  navigateFn: NavigateFn = navigate,
): Promise<PollOutcome | 'error'> {
  const result = await fetchPlayerState({
    gameCode,
    playerId,
    playerToken,
    context: 'poll-player-state',
    fetchFn,
  });
  if (result.kind === 'auth-failed' || result.kind === 'network-blip') {
    return 'error';
  }
  return applyPlayerStatePoll(result.data, navigateFn);
}
